import { Bike, Bus, Car, Footprints } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { LucideIcon, type LucideIconComponent } from '@/src/components/LucideIcon';
import { cx } from '@/styled-system/css';
import type { CommuteDestination, CommuteTime, TravelMode } from './types';
import { TRAVEL_MODE_CONFIG } from './types';
import {
  card,
  cardActive,
  cardContent,
  cardIcon,
  cardLabel,
  cardLoading,
  cardsContainer,
  cardTime,
  commuteHeader,
  commuteTitle,
  destinationText,
  editButton,
} from './commuteTimeCards.styles';

const TRAVEL_MODE_ICONS: Record<string, LucideIconComponent> = {
  car: Car,
  walk: Footprints,
  transit: Bus,
  bike: Bike,
};

type CommuteTimeCardsProps = {
  destination: CommuteDestination;
  commuteTimes: Array<CommuteTime>;
  isLoading?: boolean;
  selectedMode?: TravelMode;
  onModeSelect?: (mode: TravelMode) => void;
  onEdit?: () => void;
};

export function CommuteTimeCards({
  destination,
  commuteTimes,
  isLoading = false,
  selectedMode,
  onModeSelect,
  onEdit,
}: CommuteTimeCardsProps) {
  const t = useTranslations('hdp');
  return (
    <div>
      <div className={commuteHeader}>
        <span className={commuteTitle}>{t('commuteTimes')}</span>
        {onEdit && (
          <button type="button" className={editButton} onClick={onEdit}>
            {t('edit')}
          </button>
        )}
      </div>
      <p className={destinationText}>{t('toDestination', { name: destination.name })}</p>
      <div className={cardsContainer}>
        {TRAVEL_MODE_CONFIG.map(({ mode, labelKey, icon }) => {
          const time = commuteTimes.find((ct) => ct.mode === mode);
          const isActive = selectedMode === mode;
          const label = t(labelKey);

          return (
            <button
              key={mode}
              type="button"
              className={cx(card, isActive && cardActive)}
              onClick={() => onModeSelect?.(mode)}
              aria-pressed={isActive}
            >
              <span className={cardIcon} aria-hidden="true">
                {(() => {
                  const ModeIcon = TRAVEL_MODE_ICONS[icon];
                  return ModeIcon ? <LucideIcon icon={ModeIcon} size="md" /> : null;
                })()}
              </span>
              <span className={cx(cardContent, isLoading && cardLoading)}>
                <span className={cardTime}>{time ? time.durationText : '--'}</span>
                <span className={cardLabel}>{label}</span>
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
